import { useAuth0 } from '@auth0/auth0-react'
import {
  UseMutationOptions,
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { QueryKeys } from '../../types'
import { Screening } from './types'
import {
  getNextQuestion,
  getPreviousQuestion,
  getScreeningStatus,
  postAnswerAndGetNextQuestion,
  postStartScreening,
  patchSubmitScreening,
  deleteScreening,
  transitionFollowUpToStandard,
  getScreeningEndStatus,
} from './screening'
import { Question, ScreeningEndStatusResponse, ScreeningStatusResponse, ScreeningType } from '@estercare/ester-shared'
import { useSelectUserId } from '../../../../state/selectors'

export const screeningStatusQueryKey = [QueryKeys.Screening, 'private', 'status']

export const useGetScreeningStatusQuery = (): UseQueryResult<ScreeningStatusResponse, Error> => {
  const { getAccessTokenSilently } = useAuth0()
  const userId = useSelectUserId()

  return useQuery({
    queryKey: [...screeningStatusQueryKey, userId],
    queryFn: async (): Promise<ScreeningStatusResponse> => {
      const accessToken = await getAccessTokenSilently()
      return await getScreeningStatus({ accessToken })
    },
  })
}

export const useFetchNextQuestionMutation = (options?: UseMutationOptions<Question, Error, string>) => {
  const { getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationFn: async (screeningId: string) => {
      const accessToken = await getAccessTokenSilently()
      return await getNextQuestion({ accessToken, screeningId })
    },
    ...options,
  })
}

export const useStartScreeningMutation = (options?: UseMutationOptions<Screening, Error, string>) => {
  const { getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationFn: async (type: ScreeningType) => {
      const accessToken = await getAccessTokenSilently()
      if (!type) throw new Error('Missing required screening type')
      return await postStartScreening({ accessToken, type })
    },
    ...options,
  })
}

export const usePostAnswerAndGetNextQuestionMutation = (
  options?: UseMutationOptions<Question, Error, { screeningId: string; questionId: string; answer: string[] }>
): UseMutationResult<Question, Error, { screeningId: string; questionId: string; answer: string[] }> => {
  const { getAccessTokenSilently } = useAuth0()

  return useMutation<Question, Error, { screeningId: string; questionId: string; answer: string[] }>({
    mutationKey: [QueryKeys.Screening, 'private', 'nextQuestion'],
    mutationFn: async ({ screeningId, questionId, answer }) => {
      const accessToken = await getAccessTokenSilently()
      return await postAnswerAndGetNextQuestion({ accessToken, screeningId, questionId, answer })
    },
    ...options,
  })
}

export const useGetPreviousQuestionQuery = (screeningId?: string) => {
  const { getAccessTokenSilently } = useAuth0()
  return useQuery({
    queryKey: [QueryKeys.Screening, 'private', 'previousQuestion', screeningId],
    queryFn: async (): Promise<Question> => {
      const accessToken = await getAccessTokenSilently()
      if (!screeningId) throw new Error('Missing required screeningId')
      return await getPreviousQuestion({ accessToken, screeningId })
    },
    //No caching
    gcTime: 0,
    staleTime: 0,
    enabled: false,
  })
}

export const useGetScreeningEndStatusQuery = (
  screeningId?: string
): UseQueryResult<ScreeningEndStatusResponse, Error> => {
  const { getAccessTokenSilently } = useAuth0()
  return useQuery({
    queryKey: [QueryKeys.Screening, 'private', 'endStatus', screeningId],
    queryFn: async (): Promise<ScreeningEndStatusResponse> => {
      const accessToken = await getAccessTokenSilently()
      if (!screeningId) throw new Error('Missing required screeningId')
      return await getScreeningEndStatus({ accessToken, screeningId })
    },
    gcTime: 0,
    staleTime: 0,
    enabled: false,
  })
}

export const useSubmitScreeningQuery = (options?: UseMutationOptions<Screening, Error, { screeningId: string }>) => {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()

  return useMutation<Screening, Error, { screeningId: string }>({
    mutationKey: [QueryKeys.Screening, 'private', 'submit'],
    mutationFn: async ({ screeningId }) => {
      const accessToken = await getAccessTokenSilently()
      if (!screeningId) throw new Error('Missing required screening id')
      return await patchSubmitScreening({ accessToken, screeningId })
    },
    ...options,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: screeningStatusQueryKey })
      await options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useDeleteScreening = (options?: UseMutationOptions<void, Error, void>) => {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()

  return useMutation<void, Error, void>({
    mutationKey: [QueryKeys.Screening, 'private', 'delete'],
    mutationFn: async () => {
      const accessToken = await getAccessTokenSilently()
      await deleteScreening({ accessToken })
    },
    ...options,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: screeningStatusQueryKey })
      await options?.onSuccess?.(data, variables, context)
    },
  })
}

export const useTransitionFollowUpToStandardMutation = (
  options?: UseMutationOptions<Screening, Error, { screeningId: string }>
) => {
  const { getAccessTokenSilently } = useAuth0()

  // No status invalidation here on purpose: the caller clears the store and navigates into the
  // questionnaire, which refetches. See useTransitionFollowUpToStandard in ScreeningShared.tsx.
  return useMutation<Screening, Error, { screeningId: string }>({
    mutationKey: [QueryKeys.Screening, 'private', 'transitionFollowUpToStandard'],
    mutationFn: async ({ screeningId }) => {
      const accessToken = await getAccessTokenSilently()
      if (!screeningId) throw new Error('Missing required screening id')
      return await transitionFollowUpToStandard({ accessToken, screeningId })
    },
    ...options,
  })
}
